import * as React from 'react';
import { endOfMonth, format, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn, toClinic } from '../../lib/utils';

/**
 * Date and time fields (DS-30…33, NFR-10).
 *
 * The date field is typed as `dd/mm/yyyy` whatever the browser's locale says —
 * a native `type="date"` renders in the OS language, so a reception PC set to
 * US English showed `mm/dd/yyyy` and 03/04 meant two different days depending
 * on the machine. The text field is what reception types into; the calendar
 * button opens the browser's own picker through a hidden native input, so no
 * calendar library enters the bundle. The value in and out stays `yyyy-MM-dd`.
 * Rich month grids belong to the calendar screen, not here.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;
const ISO_EXACT = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * NFR-10 — the value is already a clinic-local date string. Parsing it into a
 * `Date` and formatting it back would push it a day either way for anyone whose
 * browser is not on Amman time, so the date part is taken as written.
 */
function toDateField(value?: string | null): string {
  if (!value) return '';
  return ISO_DATE.test(value) ? value.slice(0, 10) : '';
}

/** `yyyy-MM-dd` → `dd/mm/yyyy`, by string surgery for the same NFR-10 reason. */
function toDisplay(iso: string): string {
  const match = ISO_EXACT.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

/** Up to eight digits shown as `dd/mm/yyyy`; a slash appears only once a digit follows it. */
function maskDigits(digits: string): string {
  const d = digits.slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * Whatever was typed or pasted → the masked text. `1/5/2026` and `1.5.2026` pad
 * their short parts, so a separator typed after one digit is honoured rather than
 * swallowed into the next segment.
 */
function maskInput(raw: string): string {
  const parts = raw.replace(/[.\-\s]/g, '/').split('/');
  const padded = parts.map((part, index) => {
    const digits = part.replace(/\D/g, '');
    return index < parts.length - 1 && digits.length === 1 ? `0${digits}` : digits;
  });
  return maskDigits(padded.join(''));
}

/** A complete, real calendar day as `yyyy-MM-dd`, or null. */
function parseDisplay(text: string): string | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);
  if (month < 1 || month > 12 || day < 1 || year < 1000) return null;
  // Day 0 of the next month is the last day of this one — UTC so DST cannot shift it.
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day > daysInMonth) return null;
  return `${yyyy}-${mm}-${dd}`;
}

function clinicToday(): Date {
  return toClinic(new Date()) ?? new Date();
}

const isoDate = (date: Date): string => format(date, 'yyyy-MM-dd');

// ---------------------------------------------------------------------------
// DatePicker
// ---------------------------------------------------------------------------

export interface DatePickerProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'type' | 'size'> {
  /** Clinic-local `yyyy-MM-dd` (an ISO datetime is accepted and truncated). */
  value?: string | null;
  /** `yyyy-MM-dd`, or null when the field is emptied or not yet a whole date. */
  onChange: (value: string | null) => void;
  min?: string;
  max?: string;
  /** Truthy renders the invalid state; the message itself belongs to `FieldError`. */
  error?: boolean | string;
}

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  { value, onChange, min, max, error, className, disabled, readOnly, onBlur, ...rest },
  ref,
) {
  const { t } = useTranslation('common');
  const iso = toDateField(value);
  const [draft, setDraft] = React.useState(() => toDisplay(iso));
  // What this field last reported. A parent echoing it back must not reset a
  // half-typed draft; any other value is an outside change and replaces it.
  const reported = React.useRef(iso);
  const nativeRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (iso === reported.current) return;
    reported.current = iso;
    setDraft(toDisplay(iso));
  }, [iso]);

  const report = (next: string) => {
    if (next === reported.current) return;
    reported.current = next;
    onChange(next || null);
  };

  const handleText = (raw: string) => {
    // A `yyyy-MM-dd` paste (or a test's `fill`) is taken as the value outright.
    if (ISO_EXACT.test(raw.trim())) {
      const next = raw.trim();
      setDraft(toDisplay(next));
      report(next);
      return;
    }
    const masked = maskInput(raw);
    setDraft(masked);
    // Like the native control: anything short of a whole real day reads as empty.
    report(parseDisplay(masked) ?? '');
  };

  const openPicker = () => {
    const native = nativeRef.current;
    if (!native || disabled || readOnly) return;
    try {
      native.showPicker();
    } catch {
      // Older Safari has no showPicker; focusing the input is the closest it offers.
      native.focus();
      native.click();
    }
  };

  const incomplete = draft !== '' && parseDisplay(draft) === null;
  const outOfRange = iso !== '' && ((min && iso < min) || (max && iso > max));
  const invalid = Boolean(error) || Boolean(outOfRange);

  return (
    // A click anywhere on the field opens the calendar; the keyboard still types
    // into it, which is how reception enters a date it already knows.
    <div
      onClick={openPicker}
      className={cn(
        'input-base relative flex cursor-pointer items-center gap-1 font-mono focus-within:ring-1 focus-within:ring-blue-500',
        readOnly && 'cursor-default',
        disabled && 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500',
        invalid && 'border-red-500 focus-within:ring-red-500',
        className,
      )}
    >
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        // DS-51 — the date segments are numbers and stay left-to-right in Arabic.
        dir="ltr"
        placeholder="dd/mm/yyyy"
        maxLength={10}
        value={draft}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={invalid || incomplete ? true : undefined}
        onChange={(event) => handleText(event.target.value)}
        onBlur={(event) => {
          // A finished date is redrawn in canonical form (`1/5/2026` → `01/05/2026`).
          if (iso) setDraft(toDisplay(iso));
          onBlur?.(event);
        }}
        data-testid="date-picker"
        data-date-field=""
        className="w-full min-w-0 cursor-pointer bg-transparent placeholder:text-slate-400 read-only:cursor-default focus:outline-none disabled:cursor-not-allowed"
        {...rest}
      />
      {!readOnly && (
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label={t('ui.datePicker.openCalendar')}
          className="shrink-0 text-slate-400 hover:text-slate-600 disabled:cursor-not-allowed disabled:hover:text-slate-400"
        >
          <CalendarDays className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      {/* The picker anchors to this input, so it covers the field and drops below
          it — invisible, not `display: none`, which showPicker refuses. */}
      <input
        ref={nativeRef}
        type="date"
        tabIndex={-1}
        aria-hidden
        value={iso}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.value;
          setDraft(toDisplay(next));
          report(next);
        }}
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
      />
    </div>
  );
});

// ---------------------------------------------------------------------------
// TimePicker
// ---------------------------------------------------------------------------

export interface TimePickerProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'type' | 'size'> {
  /** `HH:mm`, 24-hour — the shape PROV-04 working hours are stored in. */
  value?: string | null;
  onChange: (value: string | null) => void;
  min?: string;
  max?: string;
  error?: boolean | string;
}

export const TimePicker = React.forwardRef<HTMLInputElement, TimePickerProps>(function TimePicker(
  { value, onChange, min, max, error, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      type="time"
      dir="ltr"
      value={value ?? ''}
      min={min}
      max={max}
      aria-invalid={error ? true : undefined}
      onChange={(event) => onChange(event.target.value || null)}
      className={cn('input-base font-mono', error && 'border-red-500 focus:ring-red-500', className)}
      {...rest}
    />
  );
});

// ---------------------------------------------------------------------------
// DateRangePicker
// ---------------------------------------------------------------------------

export interface DateRangeValue {
  from: string | null;
  to: string | null;
}

interface RangePreset {
  key: string;
  /** Key under `common:ui.datePicker` — the table is module-level, so it cannot
   *  hold a translated string of its own. */
  labelKey: string;
  build: () => DateRangeValue;
}

/**
 * INV-116 / RPT-* — every report opens on a period filter, and these five cover
 * what the doctor actually asks for. The clinic week starts Saturday, so the
 * week preset is anchored there rather than on the browser's locale.
 */
const PRESETS: RangePreset[] = [
  {
    key: 'today',
    labelKey: 'today',
    build: () => {
      const today = isoDate(clinicToday());
      return { from: today, to: today };
    },
  },
  {
    key: 'week',
    labelKey: 'thisWeek',
    build: () => {
      const today = clinicToday();
      return { from: isoDate(startOfWeek(today, { weekStartsOn: 6 })), to: isoDate(today) };
    },
  },
  {
    key: 'month',
    labelKey: 'thisMonth',
    build: () => {
      const today = clinicToday();
      return { from: isoDate(startOfMonth(today)), to: isoDate(today) };
    },
  },
  {
    key: 'last-month',
    labelKey: 'lastMonth',
    build: () => {
      const previous = subMonths(clinicToday(), 1);
      return { from: isoDate(startOfMonth(previous)), to: isoDate(endOfMonth(previous)) };
    },
  },
  {
    key: 'last-3-months',
    labelKey: 'last3Months',
    build: () => {
      const today = clinicToday();
      return { from: isoDate(subMonths(today, 3)), to: isoDate(today) };
    },
  },
];

export interface DateRangePickerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  fromLabel?: string;
  toLabel?: string;
  min?: string;
  max?: string;
  error?: boolean | string;
  disabled?: boolean;
  /** The report screens want them; a compact filter row can turn them off. */
  showPresets?: boolean;
  /**
   * DS-30 — one dense row instead of the stacked block. A table toolbar sits on
   * a single line of `sm` controls, and the captioned two-row form leaves the
   * fields floating above it; inline moves the captions onto the fields.
   */
  inline?: boolean;
  /**
   * Inline only — puts From/To back on screen as leading text instead of leaving
   * them to `title`. A filter bar that lines its controls up on one row wants
   * the caption without the extra line the stacked form spends on it.
   */
  showCaptions?: boolean;
}

export function DateRangePicker({
  value,
  onChange,
  fromLabel: fromLabelProp,
  toLabel: toLabelProp,
  min,
  max,
  error,
  disabled,
  showPresets = true,
  inline = false,
  showCaptions = false,
  className,
  ...rest
}: DateRangePickerProps) {
  const { t } = useTranslation('common');
  const fromLabel = fromLabelProp ?? t('ui.datePicker.from');
  const toLabel = toLabelProp ?? t('ui.datePicker.to');

  // `yyyy-MM-dd` sorts lexicographically, so plain string comparison is the
  // whole clamp — no parsing, no timezone in the way.
  const setFrom = (from: string | null) => {
    const next: DateRangeValue = { from, to: value.to };
    if (from && next.to && next.to < from) next.to = from;
    onChange(next);
  };

  const setTo = (to: string | null) => {
    const next: DateRangeValue = { from: value.from, to };
    if (to && next.from && to < next.from) next.to = next.from;
    onChange(next);
  };

  const activePreset = React.useMemo(() => {
    if (!value.from || !value.to) return null;
    return PRESETS.find((preset) => {
      const range = preset.build();
      return range.from === value.from && range.to === value.to;
    })?.key ?? null;
  }, [value.from, value.to]);

  const presets = showPresets ? (
    <div className={cn('flex flex-wrap items-center', inline ? 'gap-0.5' : 'gap-1')}>
      {PRESETS.map((preset) => (
        <button
          key={preset.key}
          type="button"
          disabled={disabled}
          onClick={() => onChange(preset.build())}
          className={cn(
            'btn-ghost px-1.5 py-0.5 text-[10px]',
            activePreset === preset.key && 'bg-blue-50 text-blue-700',
          )}
        >
          {t(`ui.datePicker.${preset.labelKey}`)}
        </button>
      ))}
    </div>
  ) : null;

  if (inline) {
    // The caption becomes the accessible name — a toolbar has no room for it,
    // and `dd/mm/yyyy` already says what the field is. The field keeps the
    // system height so it lines up with the selects and search box beside it.
    // `showCaptions` puts the words back inline, which costs width but no height.
    const bound = (
      caption: string,
      current: string | null,
      commit: (next: string | null) => void,
      lowest?: string,
    ) => {
      const input = (
        <DatePicker
          aria-label={caption}
          title={caption}
          value={current}
          onChange={commit}
          min={lowest}
          max={max}
          error={error}
          disabled={disabled}
          className="w-[8.5rem]"
        />
      );
      if (!showCaptions) return input;
      return (
        <label className="flex items-center gap-1.5">
          <span className="label-micro">{caption}</span>
          {input}
        </label>
      );
    };

    return (
      <div className={cn('flex flex-wrap items-center gap-1.5', className)} {...rest}>
        <div className={cn('flex items-center', showCaptions ? 'gap-2.5' : 'gap-1')}>
          {bound(fromLabel, value.from, setFrom, min)}
          {/* The dash is what tells the two fields apart; the captions do that job
              themselves once they are on screen. */}
          {!showCaptions && (
            <span aria-hidden className="text-xs text-slate-400">
              —
            </span>
          )}
          {bound(toLabel, value.to, setTo, value.from ?? min)}
        </div>
        {presets}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)} {...rest}>
      <div className="flex items-end gap-2">
        <label className="flex-1">
          <span className="label-micro mb-0.5 block">{fromLabel}</span>
          <DatePicker
            value={value.from}
            onChange={setFrom}
            min={min}
            max={max}
            error={error}
            disabled={disabled}
          />
        </label>
        <label className="flex-1">
          <span className="label-micro mb-0.5 block">{toLabel}</span>
          <DatePicker
            value={value.to}
            onChange={setTo}
            min={value.from ?? min}
            max={max}
            error={error}
            disabled={disabled}
          />
        </label>
      </div>

      {presets}
    </div>
  );
}
